import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import tippy from 'tippy.js';

export const TooltipLinkPluginKey = new PluginKey('tooltipLinkPlugin');

export type ITooltipComponent = React.FunctionComponent<{
  href: string;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: () => void;
}>;

const createTooltip = ({
  component,
  anchor,
  view,
}: {
  component: NonNullable<ITooltipComponent>;
  anchor: HTMLAnchorElement;
  view: EditorView;
}) => {
  const pos = view.posAtDOM(anchor, 0);

  if (!component) throw new Error('TooltipComponent is required');

  return tippy(anchor, {
    content: () => {
      const tooltip = document.createElement('div');
      const root = createRoot(tooltip);

      root.render(
        createElement(component, {
          href: anchor.href,
          onEdit() {
            view.dispatch(view.state.tr.setMeta(TooltipLinkPluginKey, { action: 'edit', anchor, pos }));
            root.unmount();
          },
          onDelete() {
            view.dispatch(view.state.tr.setMeta(TooltipLinkPluginKey, { action: 'delete', anchor, pos }));
            root.unmount();
          },
          onCopy() {
            view.dispatch(view.state.tr.setMeta(TooltipLinkPluginKey, { action: 'copy', anchor, pos }));
            root.unmount();
          },
        })
      );

      return tooltip;
    },
    allowHTML: true,
    interactive: true,
    duration: 100,
    animation: 'scale-subtle',
    delay: 100,
    placement: 'top',
    appendTo: () => view.dom.parentNode as HTMLElement,
    onHidden(instance) {
      instance.destroy();
    },
  });
};

export const createTooltipPlugin = ({ TooltipComponent }: { TooltipComponent: ITooltipComponent }) =>
  new Plugin({
    key: TooltipLinkPluginKey,
    view(editorView) {
      let tooltip: ReturnType<typeof createTooltip>;

      const handleMouseOver = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const anchor = target.nodeName === 'A' ? target : target.closest('a');

        if (!anchor) return;
        tooltip?.destroy();

        tooltip = createTooltip({
          component: TooltipComponent,
          anchor: anchor as HTMLAnchorElement,
          view: editorView,
        });
      };

      editorView.dom.addEventListener('mouseover', handleMouseOver);

      return {
        destroy() {
          tooltip?.destroy();
          editorView.dom.removeEventListener('mouseover', handleMouseOver);
        },
      };
    },
  });
